import React, { useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useConfirm } from '../ui/ConfirmProvider';
import { useNotifications } from '../NotificationProvider';
import { useLoad } from '../../hooks/useLoad';
import { fetchGiftShare, regenerateGiftShare, setGiftShareEnabled } from '../../services/GiftRegistryService';
import { errorMessageOf } from '../../utils/apiErrors';
import { shareUrl } from '../../utils/giftRegistry';
import type { GiftShare } from '../../types/GiftRegistry';
import '../../styles/components/GiftRegistry.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * The one private link per project: turn it on, copy it, turn it off, or make a new one (the old link then stops working).
 * Guests who open the link see the list and can reserve an item; they never see who reserved what.
 */
const ShareRegistryModal: React.FC<Props> = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <Modal open onClose={onClose} title="Share the registry" size="medium">
      <ShareBody />
    </Modal>
  );
};

const ShareBody: React.FC = () => {
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotifications();
  const load = useLoad(fetchGiftShare, []);
  const [override, setOverride] = useState<GiftShare | null>(null);
  const [busy, setBusy] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);
  const share = override ?? load.data;

  const run = async (action: () => Promise<GiftShare>, done: string, failed: string) => {
    if (busy) return;
    setBusy(true);
    try {
      setOverride(await action());
      showSuccess(done);
    } catch (err) {
      showError(errorMessageOf(err, failed));
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    if (!share) return;
    void run(
      () => setGiftShareEnabled(!share.enabled),
      share.enabled ? 'The link is switched off' : 'The link is on',
      'The link could not be changed. Please try again.',
    );
  };

  const regenerate = async () => {
    const ok = await confirm({
      title: 'Make a new link?',
      description: 'The link you shared stops working straight away. Reservations already made are kept. You will need to send the new link to your guests.',
      confirmLabel: 'Make a new link', cancelLabel: 'Keep this link', variant: 'danger',
    });
    if (ok) await run(regenerateGiftShare, 'New link ready', 'A new link could not be made. Please try again.');
  };

  const url = share?.token ? shareUrl(share.token) : '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showSuccess('Link copied');
    } catch {
      // No clipboard permission: select the text so Ctrl/Cmd+C works.
      linkRef.current?.select();
      showError('Could not copy by itself. The link is selected, press Ctrl+C or Cmd+C.');
    }
  };

  if (load.error && !share) return <LoadErrorBox what="the share link" error={load.error} onRetry={load.reload} retrying={load.loading} />;
  if (!share) return <p className="gr-note" role="status">Loading the share link...</p>;

  return (
    <div className="gr-share">
      <p className="gr-note">
        Guests open one private link, no account needed. They see your list, and can reserve an item so nobody doubles up.
        They only see "Reserved" or "Still wanted", never who reserved. Only you see the names.
      </p>

      <button
        type="button" role="switch" aria-checked={share.enabled} disabled={busy}
        className={`gr-switch${share.enabled ? ' gr-switch--on' : ''}`} onClick={toggle}
      >
        <span className="gr-switch__track" aria-hidden="true"><span className="gr-switch__thumb" /></span>
        <span>{share.enabled ? 'Guests can open the link' : 'The link is switched off'}</span>
      </button>

      {share.token ? (
        <div className="gr-share__link">
          <label htmlFor="gr-share-url" className="gr-label">Your private link</label>
          <div className="gr-share__row">
            <input id="gr-share-url" ref={linkRef} className="form-input" readOnly value={url} onFocus={e => e.currentTarget.select()} />
            <Button variant="primary" onClick={() => void copy()} disabled={!share.enabled}>Copy</Button>
          </div>
          {!share.enabled && <p className="gr-note">Switched off: anyone opening this link sees "not available". Switch it on to share it.</p>}
          {share.enabled && (
            <a className="gr-link" href={url} target="_blank" rel="noopener noreferrer">Preview the guest page</a>
          )}
          <div>
            <Button variant="outline" onClick={() => void regenerate()} disabled={busy}>Make a new link</Button>
          </div>
        </div>
      ) : (
        <p className="gr-note">Switch the link on to get your private address.</p>
      )}
    </div>
  );
};

export default ShareRegistryModal;
